/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function City() {
    const [cities, setCities] = useState([]);
    const [countries, setCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [filteredStates, setFilteredStates] = useState([]);
    const [editFilteredStates, setEditFilteredStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRecord, setNewRecord] = useState({ name: '', country_id: '', state_id: '' });
    const [selectedCity, setSelectedCity] = useState(null);
    const formRef = useRef(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ─── Filter states when country changes (Add form) ────────────────────────────

    useEffect(() => {
        if (newRecord.country_id) {
            setFilteredStates(allStates.filter(s => String(s.country_id) === String(newRecord.country_id)));
        } else {
            setFilteredStates([]);
        }
        // Reset state_id only when country changes in add form
        if (!selectedCity) {
            setNewRecord(prev => ({ ...prev, state_id: '' }));
        }
    }, [newRecord.country_id, allStates]);

    // ─── Filter states when country changes (Edit form) ──────────────────────────

    useEffect(() => {
        if (selectedCity && newRecord.country_id) {
            setEditFilteredStates(allStates.filter(s => String(s.country_id) === String(newRecord.country_id)));
        }
    }, [newRecord.country_id, selectedCity, allStates]);

    // ─── Filter & Sort ───────────────────────────────────────────────────────────

    const filtered = cities.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.state?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.country?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = React.useMemo(() => {
        let sortable = [...filtered];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = sortConfig.key === 'country' ? (a.country?.name ?? '')
                    : sortConfig.key === 'state' ? (a.state?.name ?? '')
                    : (a[sortConfig.key] ?? '');
                let bVal = sortConfig.key === 'country' ? (b.country?.name ?? '')
                    : sortConfig.key === 'state' ? (b.state?.name ?? '')
                    : (b[sortConfig.key] ?? '');
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [filtered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchCities = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/cities');
            setCities(res.data.data || res.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch cities' });
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axiosInstance.get('/api/country');
            setCountries(res.data.data || res.data || []);
        } catch { }
    };

    const fetchStates = async () => {
        try {
            const res = await axiosInstance.get('/api/state');
            setAllStates(res.data.data || res.data || []);
        } catch { }
    };

    useEffect(() => {
        fetchCities();
        fetchCountries();
        fetchStates();
    }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewRecord(prev => {
            const updated = { ...prev, [name]: value };
            // When country changes, reset state
            if (name === 'country_id') updated.state_id = '';
            return updated;
        });
    };

    const resetForm = () => {
        setNewRecord({ name: '', country_id: '', state_id: '' });
        setSelectedCity(null);
        setFilteredStates([]);
        setEditFilteredStates([]);
        if (formRef.current) formRef.current.classList.remove('was-validated');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add('was-validated');
            return;
        }
        try {
            await axiosInstance.post('/api/cities', newRecord);
            Swal.fire({ icon: 'success', title: 'Created!', text: 'City added successfully.', timer: 1500, showConfirmButton: false });
            window.$('#add_city').modal('hide');
            resetForm();
            fetchCities();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create city' });
        }
    };

    const handleEditClick = (city) => {
        setSelectedCity(city);
        const countryId = city.country_id || city.country?.id || '';
        const stateId = city.state_id || city.state?.id || '';
        setNewRecord({ name: city.name || '', country_id: countryId, state_id: stateId });
        // Pre-populate filtered states for edit
        if (countryId) {
            setEditFilteredStates(allStates.filter(s => String(s.country_id) === String(countryId)));
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCity) return;
        try {
            await axiosInstance.put(`/api/cities/${selectedCity.id}`, newRecord);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'City updated successfully.', timer: 1500, showConfirmButton: false });
            window.$('#edit_city').modal('hide');
            resetForm();
            fetchCities();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update city' });
        }
    };

    const handleDelete = async (city) => {
        const result = await Swal.fire({
            title: 'Delete City?',
            text: `Are you sure you want to delete "${city.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/cities/${city.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchCities();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete city' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries</div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)}><i className="fa fa-angle-double-left"></i></button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}><i className="fa fa-angle-double-right"></i></button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Location Management</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-primary" href="#" data-bs-toggle="modal" data-bs-target="#add_city" onClick={resetForm}>
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add City
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/country">Country</a></li>
                                            <li><a href="/state">State</a></li>
                                            <li><a href="/city" className="active">City</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text" className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }} placeholder="Search cities..."
                                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>City Name{getSortIcon('name')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('state')}>State{getSortIcon('state')}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('country')}>Country{getSortIcon('country')}</th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="5" className="text-center"><div className="spinner-border" role="status"></div></td></tr>
                                                ) : currentItems.length > 0 ? (
                                                    currentItems.map((city, index) => (
                                                        <tr key={city.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>{city.name || 'N/A'}</td>
                                                            <td>{city.state?.name || 'N/A'}</td>
                                                            <td>{city.country?.name || 'N/A'}</td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-success-light me-2" style={{ cursor: 'pointer' }}
                                                                    data-bs-toggle="modal" data-bs-target="#edit_city"
                                                                    onClick={() => handleEditClick(city)} title="Edit">
                                                                    <i className="fa fa-edit"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-danger-light" style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(city)} title="Delete">
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="5" className="text-center">No cities found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <Footer />
            </div>

            {/* Add City Modal */}
            <div className="modal custom-modal fade" id="add_city" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-map-marker-alt me-2 text-primary"></i>Add City</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Country <span className="text-danger">*</span></label>
                                            <select name="country_id" value={newRecord.country_id} onChange={handleChange}
                                                className="form-control" required>
                                                <option value="">Select Country</option>
                                                {countries.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                            <div className="invalid-feedback">Please select a country.</div>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>State <span className="text-danger">*</span></label>
                                            <select name="state_id" value={newRecord.state_id} onChange={handleChange}
                                                className="form-control" required disabled={!newRecord.country_id}>
                                                <option value="">
                                                    {newRecord.country_id ? 'Select State' : 'Select Country First'}
                                                </option>
                                                {filteredStates.map(s => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </select>
                                            <div className="invalid-feedback">Please select a state.</div>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>City Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                                className="form-control" placeholder="e.g. Coimbatore" required />
                                            <div className="invalid-feedback">City name is required.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">Add City</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit City Modal */}
            <div className="modal custom-modal fade" id="edit_city" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0"><i className="fa fa-edit me-2 text-primary"></i>Edit City</h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Country <span className="text-danger">*</span></label>
                                        <select name="country_id" value={newRecord.country_id} onChange={handleChange}
                                            className="form-control">
                                            <option value="">Select Country</option>
                                            {countries.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>State <span className="text-danger">*</span></label>
                                        <select name="state_id" value={newRecord.state_id} onChange={handleChange}
                                            className="form-control" disabled={!newRecord.country_id}>
                                            <option value="">
                                                {newRecord.country_id ? 'Select State' : 'Select Country First'}
                                            </option>
                                            {editFilteredStates.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>City Name <span className="text-danger">*</span></label>
                                        <input type="text" name="name" value={newRecord.name} onChange={handleChange}
                                            className="form-control" placeholder="e.g. Coimbatore" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">Cancel</button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">Update City</button>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default City;